import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, getToken } from '../api';

const PROMPT_KEY  = 'last_prompt_shown';
const DISMISS_KEY = 'prompt_dismissed_count';
const PHOTO_KEY   = 'profile_needs_photo';

const daysSince = (iso) => iso ? (Date.now() - new Date(iso).getTime()) / 86400000 : 999;

const ICONS = { photo: '◎', birth: '✦', questions: '◉', none: '' };

const SECTION_LABELS = {
  photo: 'Add photo',
  birth: 'Birth data',
  lifestyle: 'Lifestyle answers',
  dosha: 'Bio-harmonic profile',
  relationship_intention: 'Relationship intention',
  conflict_style: 'Conflict style',
  location_preference: 'Location preference',
  intimacy_gateway: 'Intimacy profile',
  love_language: 'Love language',
};

const SECTION_ROUTES = {
  photo: null,
  birth: '/onboarding/birth',
  lifestyle: '/onboarding/quick',
  dosha: '/onboarding/deep',
  relationship_intention: '/onboarding/quick',
  conflict_style: '/onboarding/quick',
  location_preference: '/onboarding/quick',
  intimacy_gateway: '/onboarding/deep',
  love_language: '/onboarding/quick',
};

const compressImage = (file) =>
  new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const size = Math.min(img.width, img.height);
      const canvas = document.createElement('canvas');
      canvas.width = 800; canvas.height = 800;
      canvas.getContext('2d').drawImage(img, (img.width - size) / 2, (img.height - size) / 2, size, size, 0, 0, 800, 800);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.src = url;
  });

export default function ProfilePrompt({ onCompletenessLoad }) {
  const navigate = useNavigate();
  const [completeness, setCompleteness] = useState(null);
  const [visible, setVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!getToken()) return;
    const timer = setTimeout(async () => {
      try {
        const data = await api.get('/profile/completeness');
        setCompleteness(data);
        if (onCompletenessLoad) onCompletenessLoad(data);

        const needsPhoto = data.missing_sections.includes('photo');
        localStorage.setItem(PHOTO_KEY, needsPhoto ? '1' : '0');

        if (data.overall_score >= 85 || data.next_prompt.type === 'none') return;

        const lastShown   = localStorage.getItem(PROMPT_KEY);
        const dismissCount = parseInt(localStorage.getItem(DISMISS_KEY) || '0');
        const minDays = dismissCount >= 3 ? 7 : 3;
        if (daysSince(lastShown) >= minDays) setVisible(true);
      } catch {}
    }, 2000);
    return () => clearTimeout(timer);
  }, [onCompletenessLoad]);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(PROMPT_KEY, new Date().toISOString());
    const n = parseInt(localStorage.getItem(DISMISS_KEY) || '0');
    localStorage.setItem(DISMISS_KEY, String(n + 1));
  };

  const handleContinue = () => {
    if (!completeness) return;
    const { type } = completeness.next_prompt;
    if (type === 'photo') { fileRef.current?.click(); return; }
    dismiss();
    if (type === 'birth') { navigate('/onboarding/birth'); return; }
    navigate(completeness.next_prompt.section === 'dosha' ? '/onboarding/deep' : '/onboarding/quick');
  };

  const handlePhotoSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const base64 = await compressImage(file);
      await api.post('/profile', { photo_url: base64 });
      localStorage.setItem(PHOTO_KEY, '0');
      localStorage.setItem(DISMISS_KEY, '0');
      // Don't show again for 7 days after completing
      localStorage.setItem(PROMPT_KEY, new Date(Date.now() + 7 * 86400000).toISOString());
      dismiss();
    } catch {
      setUploading(false);
    }
  };

  if (!completeness || completeness.overall_score >= 85) return null;

  const { next_prompt, overall_score } = completeness;
  if (next_prompt.type === 'none') return null;

  return (
    <>
      <div className={`profile-prompt-card${visible ? ' visible' : ''}`}>
        <div className="profile-prompt-progress">
          <div className="profile-prompt-progress-fill" style={{ width: `${overall_score}%` }} />
        </div>
        <div className="profile-prompt-body">
          <span className="profile-prompt-icon">{ICONS[next_prompt.type] || '◉'}</span>
          <span className="profile-prompt-text">{next_prompt.message}</span>
        </div>
        <div className="profile-prompt-actions">
          <button className="profile-prompt-later" onClick={dismiss}>Later</button>
          <button className="profile-prompt-continue" onClick={handleContinue} disabled={uploading}>
            {uploading ? '...' : 'Continue →'}
          </button>
        </div>
      </div>
      <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoSelect} />
    </>
  );
}

// Exported for Settings screen use
export { SECTION_LABELS, SECTION_ROUTES, compressImage };
